import React, { useCallback, useRef } from 'react';

import { useSnackbar } from 'notistack';

import api from 'utils/api';

import { Mui } from 'components';

import useFetch from './useFetch';

const FINISHED_STATUSES = ['completed', 'errored', 'timeout'];

const getJobTitle = job => (job?.typeDisplay ? `Atividade ${job.typeDisplay}` : 'Atividade');

const renderCompletedMessage = job => {
  const title = getJobTitle(job);

  if (job?.outputFile) {
    return (
      <span>
        {title} finalizada com sucesso!{' '}
        <Mui.Link
          color="inherit"
          href={job.outputFile}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'underline' }}>
          Clique aqui
        </Mui.Link>{' '}
        para baixar o arquivo
      </span>
    );
  }
  return `${title} finalizada com sucesso!`;
};

const renderErroredMessage = job => {
  const title = getJobTitle(job);
  const rawErrors = job?.outputJson?.errors;
  if (rawErrors) {
    const errors = Array.isArray(rawErrors) ? rawErrors.join(', ') : rawErrors;
    return `${title} finalizada com erro! ${errors}`;
  }
  return `${title} finalizada com erro!`;
};

const renderJobMessage = job => {
  const title = getJobTitle(job);

  switch (job?.status) {
    case 'completed':
      return renderCompletedMessage(job);
    case 'errored':
      return renderErroredMessage(job);
    case 'timeout':
      return `Aviso: ${title} excedeu o tempo limite.`;
    default:
      return `${title} foi finalizada.`;
  }
};

const getSnackbarVariant = status => {
  if (status === 'completed') return 'success';
  if (status === 'errored') return 'error';
  if (status === 'timeout') return 'warning';
  return 'info';
};

const useJobNotification = ({ enabled = true } = {}) => {
  const { enqueueSnackbar } = useSnackbar();

  const prevJobsRef = useRef([]);
  const callbacksRef = useRef(new Map());

  const runJobCallback = useCallback((uid, data) => {
    if (!uid) return;
    const callback = callbacksRef.current.get(uid);
    if (callback) {
      callbacksRef.current.delete(uid);
      callback(data, null);
    }
  }, []);

  const notifyJob = useCallback(
    job => {
      if (!job || !FINISHED_STATUSES.includes(job.status)) return;
      const message = renderJobMessage(job);
      const variant = getSnackbarVariant(job.status);
      enqueueSnackbar(message, { variant });
    },
    [enqueueSnackbar],
  );

  const notifyFinishedJob = useCallback(
    async job => {
      if (!job.uid) return;
      try {
        const { data } = await api.get(`/commons/jobs/${job.uid}/`);
        notifyJob(data);
        runJobCallback(job.uid, data);
      } catch {
        notifyJob({ ...job });
        runJobCallback(job.uid, job);
      }
    },
    [notifyJob, runJobCallback],
  );

  const { data, isLoading, error, mutate } = useFetch(
    [enabled && '/commons/jobs', { status: 'queued,processing' }],
    {
      refreshInterval: () => (prevJobsRef.current.length > 0 ? 3000 : 0),
      onSuccess: response => {
        const results = response?.results || [];
        const resultUids = new Set(results.map(job => job.uid));

        const finishedJobs = prevJobsRef.current.filter(prevJob => !resultUids.has(prevJob.uid));
        finishedJobs.forEach(job => notifyFinishedJob(job));

        prevJobsRef.current = results;
      },
    },
  );

  const { results = [], count = 0 } = data || {};

  const trackJob = useCallback(
    (jobUid, { onComplete } = {}) => {
      if (!jobUid) return;
      if (onComplete) {
        callbacksRef.current.set(jobUid, onComplete);
      }
      if (!prevJobsRef.current.some(prevJob => prevJob.uid === jobUid)) {
        prevJobsRef.current = [...prevJobsRef.current, { uid: jobUid, status: 'queued' }];
      }
      mutate();
    },
    [mutate],
  );

  return {
    pendingJobs: results,
    count: Math.max(count, prevJobsRef.current.length),
    trackJob,
    mutate,
    isLoading,
    error,
  };
};

export default useJobNotification;
